/**
 * @description Shared SEO/AEO data for the Chief in Tech Capital fund site
 * (chiefintech.capital). Dependency-free so it can be imported by BOTH the
 * server head injector / sitemap routes AND the client fund pages.
 *
 * Single source of truth for: per-page titles + meta descriptions, the public
 * URL inventory (sitemap), FAQ content (also rendered as FAQPage JSON-LD),
 * and the fund's Organization JSON-LD entity data.
 */

import {
  STARTUP_BENEFIT_CATEGORY_PAGES,
  STARTUP_BENEFITS_PARTNER_PATH,
} from "./startupBenefitsPublic";

export const CAPITAL_BASE_URL = "https://chiefintech.capital";
export const CAPITAL_SITE_NAME = "Chief in Tech Capital";
export const CAPITAL_OG_IMAGE =
  "https://www.chiefintech.com/uploads/brands/capital-1767644687759-641799776.png";

/** Root/homepage title — used verbatim (no suffix appended). */
export const CAPITAL_ROOT_TITLE =
  "Chief in Tech Capital | Enterprise Systems VC Fund";

export const CAPITAL_DEFAULT_DESCRIPTION =
  "Chief in Tech Capital is a Palo Alto pre-seed and seed fund backing AI builders and operators-turned-founders creating enterprise systems.";

export const capitalEcosystemTrackRecord = {
  startupsSurfaced: 107,
  capitalRaised: "$850M+",
  ecosystemValue: "$5B+",
  sourceName: "WomenTech",
  sourceSince: 2015,
  performanceNote: "These are ecosystem cohort figures, not Fund I performance.",
} as const;

export const capitalEcosystemTrackRecordAttribution =
  `Since ${capitalEcosystemTrackRecord.sourceSince}, ${capitalEcosystemTrackRecord.sourceName} sourced ${capitalEcosystemTrackRecord.startupsSurfaced} startups. Those companies raised ${capitalEcosystemTrackRecord.capitalRaised} and reached ${capitalEcosystemTrackRecord.ecosystemValue} in value. ${capitalEcosystemTrackRecord.performanceNote}`;

export interface CapitalPageMeta {
  /** Page title WITHOUT the " | Chief in Tech Capital" suffix. */
  title: string;
  description: string;
  /** Include in the public sitemap (default true). */
  indexable?: boolean;
}

/**
 * Per-page meta, keyed by the CLEAN public path as served on
 * chiefintech.capital (e.g. "/fund/thesis"). The root "/" is the same page
 * as "/fund".
 */
export const capitalPageMeta: Record<string, CapitalPageMeta> = {
  "/": {
    title: CAPITAL_ROOT_TITLE,
    description: CAPITAL_DEFAULT_DESCRIPTION,
  },
  "/fund": {
    title: CAPITAL_ROOT_TITLE,
    description: CAPITAL_DEFAULT_DESCRIPTION,
  },
  "/fund/thesis": {
    title: "Investment Thesis: AI-Enabled Enterprise Systems",
    description:
      "We back AI builders and operators-turned-founders creating enterprise systems, sourced and supported through a trusted technology and executive network.",
  },
  "/fund/community": {
    title: "Community Network: Founders, Talent & Experts",
    description:
      "Join the Chief in Tech Capital sourcing and support network as a founder, portfolio operator, advisor, mentor, legal or finance partner, or trusted ecosystem connector.",
  },
  "/events": {
    title: "Events: Enterprise Tech, Startups & Investors",
    description:
      "A curated calendar of US enterprise technology, B2B marketing, startup, and investor events selected by Chief in Tech Capital.",
  },
  "/fund/portfolio": {
    title: "Portfolio: Enterprise Systems in Practice",
    description:
      "Meet companies backed by Chief in Tech Capital through current first-party product and founder context plus publicly disclosed investor context.",
  },
  "/fund/team": {
    title: "Team: Managing Directors",
    description:
      "Meet Chief in Tech Capital Managing Directors Anna Radulovski and Ivo Radulovski: operators, exited founders, investors, and builders of a 200,000-member tech ecosystem.",
  },
  "/fund/advisory-board": {
    title: "Advisory Board",
    description:
      "Sitting and former C-level executives from Fortune 500 and high-growth tech companies who advise Chief in Tech Capital and mentor our portfolio founders.",
  },
  "/fund/track-record": {
    title: `${capitalEcosystemTrackRecord.sourceName} Ecosystem Record: ${capitalEcosystemTrackRecord.startupsSurfaced} Startups, ${capitalEcosystemTrackRecord.capitalRaised} Raised`,
    description: capitalEcosystemTrackRecordAttribution,
  },
  "/fund/insights": {
    title: "Insights",
    description:
      "Perspectives from Chief in Tech Capital on AI-enabled enterprise systems, operator-led company building, and pre-seed and seed investing.",
  },
  "/fund/faq": {
    title: "FAQ: Investing, Pitching & Fund Structure",
    description:
      "Answers for founders and LPs: check size, stage focus, investment process, fund structure and economics, 506(c) accredited investor requirements, and how to pitch.",
  },
  "/fund/contact": {
    title: "Contact: Partner With Us",
    description:
      "Connect with Chief in Tech Capital as an investor or founder, or join the private sourcing and support network as talent, a mentor, advisor, or ecosystem partner.",
  },
  "/privacy": {
    title: "Privacy Policy",
    description: "How Chief in Tech LLC handles personal data across the Chief in Tech Capital website and related services.",
    indexable: false,
  },
  "/terms": {
    title: "Terms of Service",
    description: "Terms governing use of the Chief in Tech Capital website and related Chief in Tech LLC services.",
    indexable: false,
  },
  "/cookies": {
    title: "Cookie Policy and Analytics Settings",
    description: "Review and change optional analytics settings for the Chief in Tech Capital website.",
    indexable: false,
  },
  "/accessibility": {
    title: "Accessibility Statement",
    description: "Accessibility information and support for the Chief in Tech Capital website.",
    indexable: false,
  },
  "/fund/risk-factors": {
    title: "Risk Factors and Investment Considerations",
    description:
      "Risk factors and investment considerations for prospective Limited Partners of Chief in Tech Capital under SEC Rule 506(c).",
  },
  "/fund/for-founders": {
    title: "For Founders: Pitch Chief in Tech Capital",
    description:
      "We invest $50K to $250K at pre-seed and seed in AI-enabled enterprise systems. Review our criteria and submit your pitch.",
  },
  "/fund/support-lps": {
    title: "For LPs: Partnering With the Fund",
    description:
      "How Limited Partners engage with Chief in Tech Capital beyond capital: deal flow, advisory panels, community access, and co-investment opportunities.",
  },
  "/fund/apply/founder": {
    title: "Submit Your Pitch",
    description:
      "Pitch your AI-enabled enterprise-systems startup to Chief in Tech Capital: upload your deck and complete the founder application.",
  },
  "/fund/apply/investor": {
    title: "Apply as an Investor",
    description:
      "Apply to invest with Chief in Tech Capital. Open to accredited investors under SEC Rule 506(c) of Regulation D.",
  },
  "/fund/apply/recommend": {
    title: "Recommend a Startup",
    description:
      "Know an exceptional founder? Recommend a startup to Chief in Tech Capital and help us back proven builders before the market sees them.",
    indexable: false,
  },
  "/startup-benefits": {
    title: "Startup Benefits & Credits (2026)",
    description:
      "Compare startup benefits, software deals, cloud credits, founder discounts, and partner programs with eligibility, access routes, limits, and source links.",
  },
  [STARTUP_BENEFITS_PARTNER_PATH]: {
    title: "Become a Startup Benefits Partner",
    description:
      "Propose a founder benefit for private review across Chief in Tech Capital, AI Builders Network, and WomenTech Network, including eligibility, access, and commercial model.",
  },
  ...Object.fromEntries(
    STARTUP_BENEFIT_CATEGORY_PAGES.map((page) => [
      page.path,
      { title: page.title, description: page.description },
    ]),
  ),
};

const STARTUP_BENEFIT_DETAIL_PATH = /^\/startup-benefits\/([a-z0-9]+(?:-[a-z0-9]+)*)$/;

/** Retired public URLs and their canonical replacements on chiefintech.capital. */
export const capitalLegacyRedirects: Readonly<Record<string, string>> = {
  "/submit-benefit": STARTUP_BENEFITS_PARTNER_PATH,
  "/fund/events": "/events",
  "/fund/overview": "/fund",
  "/fund/details": "/fund/faq",
  "/fund/why-invest": "/fund/thesis",
  "/fund/advantage": "/fund/thesis",
  "/fund/cohorts": "/fund/community",
  "/fund/network": "/fund/community",
  "/fund/support-advisors": "/fund/community",
};

/** Main-host Capital aliases and their canonical public or private paths. */
export const capitalMainHostApplicationAliases: Readonly<Record<string, string>> = {
  "/capital/pitch-startup": "/fund/apply/founder",
  "/capital/investor-apply": "/fund/apply/investor",
  "/capital/recommend-startup": "/fund/apply/recommend",
  "/capital/deal-readiness": "/fund/deal-readiness",
  "/capital/dealroom": "/fund/dealroom",
  "/capital/portfolio-hub": "/fund/portfolio-hub",
  "/capital/risk-factors": "/fund/risk-factors",
};

/** Public, indexable URLs for the chiefintech.capital sitemap. */
export function capitalSitemapPaths(): string[] {
  return Object.entries(capitalPageMeta)
    .filter(([path, meta]) => path !== "/fund" && meta.indexable !== false)
    .map(([path]) => path);
}

const capitalOperationalPaths = new Set([
  "/fund/funding-rounds",
  "/fund/dealroom",
  "/fund/deal-readiness",
  "/fund/portfolio-hub",
  "/fund/old",
  "/fund/jury",
  "/fund/lp",
  "/fund/lp/referrals",
  "/fund/lp/nominate",
  "/fund/lp/brand-hub",
  "/fund/lp/directory",
]);

const capitalPrivatePageMeta: CapitalPageMeta = {
  title: "Chief in Tech Capital Workspace",
  description:
    "Private Chief in Tech Capital account and fund operations workspace.",
  indexable: false,
};

const capitalAccountPathPrefixes = [
  "/login",
  "/register",
  "/profile",
  "/settings",
  "/onboarding",
  "/forgot-password",
  "/reset-password",
  "/verify-2fa",
  "/verify-email",
];

/**
 * Fail-closed inventory for browser routes accepted on the Capital hostname.
 * API and static-asset paths are handled separately by the hostname router.
 */
export function isKnownCapitalAppPath(cleanPath: string): boolean {
  const stripped = cleanPath.replace(/\/+$/, "") || "/";
  if (
    capitalPageMeta[stripped] ||
    capitalLegacyRedirects[stripped] ||
    capitalOperationalPaths.has(stripped)
  ) {
    return true;
  }
  if (STARTUP_BENEFIT_DETAIL_PATH.test(stripped)) {
    return true;
  }
  return capitalAccountPathPrefixes.some(
    (prefix) => stripped === prefix || stripped.startsWith(`${prefix}/`),
  );
}

/**
 * Resolve the canonical public Capital path represented by a browser/server
 * path. The main application keeps Fund routes under /capital/fund, while the
 * Capital hostname exposes their public paths. Unknown paths fail closed so
 * they cannot inherit indexable fund-home metadata.
 */
export function resolveCapitalDocumentMetaPath(
  cleanPath: string,
  capitalHost: boolean,
): string | null {
  const normalized = cleanPath.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  let publicPath: string;
  if (capitalHost) {
    publicPath = normalized;
  } else if (capitalMainHostApplicationAliases[normalized]) {
    publicPath = capitalMainHostApplicationAliases[normalized];
  } else if (normalized === "/capital/fund") {
    publicPath = "/fund";
  } else if (normalized.startsWith("/capital/fund/")) {
    const suffix = normalized.slice("/capital/fund".length);
    publicPath = suffix === "/events" ? "/events" : `/fund${suffix}`;
  } else {
    return null;
  }

  const canonicalPath = capitalLegacyRedirects[publicPath] || publicPath;
  return isKnownCapitalAppPath(canonicalPath) ? canonicalPath : null;
}

/**
 * Resolve meta for a clean public path. Unknown and private application paths
 * are deliberately noindex; callers should use resolveCapitalDocumentMetaPath
 * first when deciding whether a request belongs to the Capital application.
 */
export function resolveCapitalPageMeta(cleanPath: string): CapitalPageMeta & { canonical: string } {
  const stripped = cleanPath.replace(/\/+$/, "");
  const redirected = capitalLegacyRedirects[stripped] || stripped;
  const normalized = redirected === "" || redirected === "/fund" ? "/" : redirected;
  const isStartupBenefitDetail = STARTUP_BENEFIT_DETAIL_PATH.test(normalized);
  const isPrivatePath = capitalOperationalPaths.has(normalized) || capitalAccountPathPrefixes.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
  const meta = capitalPageMeta[normalized] || (isStartupBenefitDetail
    ? {
        title: "Startup Benefit for Founders",
        description:
          "Review this startup benefit's provider, eligibility, access route, limitations, source-check date, and primary source.",
      }
    : isPrivatePath
      ? capitalPrivatePageMeta
      : {
          title: "Chief in Tech Capital",
          description: CAPITAL_DEFAULT_DESCRIPTION,
          indexable: false,
        });
  return {
    ...meta,
    canonical: `${CAPITAL_BASE_URL}${normalized === "/" ? "/" : normalized}`,
  };
}

/** Whether a route has reviewed, canonical metadata instead of fallback metadata. */
export function hasCanonicalCapitalPageMeta(cleanPath: string): boolean {
  const stripped = cleanPath.split("?")[0].split("#")[0].replace(/\/+$/, "");
  const redirected = capitalLegacyRedirects[stripped] || stripped;
  const normalized = redirected === "" || redirected === "/fund" ? "/" : redirected;
  return Object.prototype.hasOwnProperty.call(capitalPageMeta, normalized);
}

/** Full <title> text for a clean path (suffix appended except on the root). */
export function capitalPageTitle(cleanPath: string): string {
  const meta = resolveCapitalPageMeta(cleanPath);
  return meta.title === CAPITAL_ROOT_TITLE
    ? CAPITAL_ROOT_TITLE
    : `${meta.title} | ${CAPITAL_SITE_NAME}`;
}

/* ------------------------------------------------------------------ */
/*  Entity data (JSON-LD)                                              */
/* ------------------------------------------------------------------ */

export const capitalTeamEntities = [
  {
    name: "Anna Radulovski",
    jobTitle: "Managing Director & Co-Founder",
    sameAs: "https://www.linkedin.com/in/annaradulovski/",
  },
  {
    name: "Ivo Radulovski",
    jobTitle: "Managing Director & Co-Founder",
    sameAs: "https://www.linkedin.com/in/radulovski/",
  },
];

/** Organization JSON-LD for Chief in Tech Capital (stringified by caller). */
export function capitalOrganizationJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: CAPITAL_SITE_NAME,
    alternateName: "Chief in Tech Capital Fund",
    url: CAPITAL_BASE_URL,
    logo: CAPITAL_OG_IMAGE,
    description: CAPITAL_DEFAULT_DESCRIPTION,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Palo Alto",
      addressRegion: "CA",
      addressCountry: "US",
    },
    founder: capitalTeamEntities.map((m) => ({
      "@type": "Person",
      name: m.name,
      jobTitle: m.jobTitle,
      sameAs: m.sameAs,
    })),
    parentOrganization: {
      "@type": "Organization",
      name: "Chief in Tech",
      url: "https://www.chiefintech.com",
    },
    sameAs: [
      "https://www.chiefintech.com/capital/fund",
      "https://www.linkedin.com/company/chief-in-tech-global-media",
    ],
    knowsAbout: [
      "Venture Capital",
      "Pre-Seed Investing",
      "Seed Investing",
      "Artificial Intelligence",
      "Enterprise Systems",
      "Enterprise Software",
      "AI Agents",
      "AI Infrastructure",
      "Systems of Record",
      "Systems of Engagement",
      "Systems of Intelligence",
    ],
  };
}

/** FAQPage JSON-LD for /fund/faq (stringified by caller). */
export function capitalFaqJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: capitalFaqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

/* ------------------------------------------------------------------ */
/*  FAQ content — single source of truth (rendered on /fund/faq and    */
/*  emitted as FAQPage JSON-LD by the server head injector)            */
/* ------------------------------------------------------------------ */

export const capitalFaqItems = [
  // --- Founder FAQs ---
  {
    question: "What does Chief in Tech invest in?",
    answer:
      "We back AI builders and operators-turned-founders creating enterprise systems at pre-seed and seed. We look for products that own or materially transform a critical workflow, compound useful context, and have a credible path to trusted production use. Healthcare is in scope when it meets the same structural criteria.",
  },
  {
    question: "Who do you back?",
    answer:
      "Two founder cohorts anchor our thesis. Operators-turned-founders bring lived knowledge of the workflow, buyer, and deployment constraints. AI builders turn technical capability into production systems with durable enterprise value. AI builders may be technical, product, or domain builders.",
  },
  {
    question: "What stage does the fund target?",
    answer:
      "Chief in Tech is a pre-seed and seed fund. We invest early, where our network advantage and conviction can create the greatest edge.",
  },
  {
    question: "What is your typical check size?",
    answer:
      "Our initial checks range from $50K to $250K. We may reserve capital for follow-on investments and exercise pro-rata rights subject to the fund's portfolio strategy and governing documents.",
  },
  {
    question: "Do you lead rounds?",
    answer:
      "We can lead or co-lead pre-seed rounds. At seed stage, we typically co-invest alongside a lead investor but can take active roles in syndicate formation.",
  },
  {
    question: "What does your investment process look like?",
    answer:
      "Structured but fast: thesis-fit screen → intro call → deep dive (product, market, team) → Investment Committee review → decision. Founders get direct access to decision-makers, and we can move from first meeting to decision within days when a round is competitive; a typical process completes in 2–4 weeks.",
  },
  {
    question: "How do I pitch my startup?",
    answer:
      "Submit your pitch at chiefintech.capital/fund/apply/founder. Upload your deck and the form pre-fills for you. We review every submission and aim to respond within one week.",
  },
  {
    question: "Do I need to be a woman to receive funding?",
    answer:
      "No. Investment decisions are based on the enterprise-systems thesis, founder capability, company evidence, and fund fit. Founder and team composition is measured and reported as an outcome of our sourcing network, not used as a public category or portfolio target.",
  },
  {
    question: "Do you invest in founders from different backgrounds?",
    answer:
      "Yes. We welcome qualified founders from all backgrounds. We evaluate whether the team fits one or both founder cohorts, understands the enterprise problem, can execute, and is building a company that meets the fund's structural investment criteria.",
  },
  // --- LP FAQs ---
  {
    question: "What is the fund structure?",
    answer:
      "Chief in Tech Capital, a Series of Decile Start Fund, LP, is a $5M venture fund. Fund administration is provided through Decile Group. Definitive legal and economic terms are contained in the governing fund documents provided to qualified prospective investors.",
  },
  {
    question: "What are the fund economics?",
    answer:
      "Clean 2/20 economics: 2% management fee and 20% carried interest. The fund is designed for efficiency with institutional-grade operations through our partnership with Decile Group.",
  },
  {
    question: "Who can invest in the fund?",
    answer:
      "The fund accepts investments from accredited investors in the United States under SEC Rule 506(c) of Regulation D. We are not currently accepting investors from the European Union, India, or Singapore due to regulatory considerations.",
  },
  {
    question: "What does 'accredited investor' mean?",
    answer:
      "Under SEC rules, an accredited investor is generally an individual with income over $200,000 (or $300,000 jointly with a spouse) in each of the last two years, or a net worth over $1 million excluding their primary residence, or someone holding certain professional licenses (Series 7, 65, or 82). Entities can qualify through assets over $5 million or if all owners are accredited. Under Rule 506(c), accreditation must be verified before investing.",
  },
  {
    question: "What reporting will I receive as an LP?",
    answer:
      "LPs receive quarterly portfolio updates, annual audited financial statements, K-1 tax documents, and ad-hoc updates on material portfolio events. All documents are accessible through the secure Decile Group LP portal.",
  },
  {
    question: "What is the fund term?",
    answer:
      "The fund has a 10-year term with optional 1-year extensions. We expect the primary investment period to span the first 3-4 years, with the remaining period focused on supporting portfolio companies and managing exits.",
  },
  // --- General / Differentiation ---
  {
    question: "What makes Chief in Tech differentiated?",
    answer:
      "Our edge is a network most investors can't replicate: 2,000+ C-level tech leaders plus a 200,000-member community. It surfaces exceptional founders before they raise, and it gives the companies we back direct access to the advisors, customers, and enterprise clients they need to scale. We invest alongside leading VCs and bring that network with every check.",
  },
      {
        question: "Who do you co-invest with?",
        answer:
          "Smore Labs currently identifies Adverb Ventures, Hustle Fund, Jetstream VC, Looking Glass Capital, TheFounderVC, and XFactor as investors on its public company page. Investor names are shown for context only and do not indicate endorsement, future participation, investment quality, or performance.",
  },
  {
    question: "What counts as an enterprise system?",
    answer:
      "We look for software that owns or materially transforms a critical enterprise workflow and can become part of an organization's operating fabric. Strong candidates connect systems of record, engagement, or intelligence, compound useful context, and can meet the security, governance, reliability, and buyer requirements for production use.",
  },
  {
    question: "Why focus on enterprise systems?",
    answer:
      "AI is collapsing systems of record, engagement, and intelligence into a new application layer. Our technology and executive network helps us test buyer urgency, budget ownership, deployment friction, governance requirements, and the path from product capability to trusted production use.",
  },
  {
    question: "Why now?",
    answer:
      "AI is driving a major platform shift across the systems people and businesses rely on every day. We believe the strongest opportunities will increasingly emerge through specialized communities and operator networks before broader venture markets fully price them in.",
  },
  {
    question: "What proof points support the ecosystem?",
    answer: `${capitalEcosystemTrackRecordAttribution} AI Builders Network represents forward sourcing capacity rather than part of this historical cohort.`,
  },
  {
    question: "How does Chief in Tech support portfolio companies?",
    answer:
      "We provide more than capital. Through our network of 2,000+ tech executives, founders gain advisors and mentors, warm paths to early customers and enterprise clients, visibility across a 200,000-member community, and follow-on fundraising support. The network that surfaced the deal stays with the founder long after the check clears.",
  },
  {
    question: "How can I join the Chief in Tech Capital community network?",
    answer:
      "Visit chiefintech.capital/fund/community and register the role that best fits you: portfolio talent, advisor, mentor, legal partner, finance and accounting partner, or ecosystem partner. Founders can pitch directly, and signed-in community members can recommend a startup. Interest records stay private, and introductions require separate consent.",
  },
  {
    question: "What is your relationship with WomenTech Network?",
    answer:
      "WomenTech Network is a 200,000+ member global community of tech professionals co-founded by our Managing Directors. The network serves as our primary proprietary deal flow channel, providing early access to founders, senior advisors, and operator talent.",
  },
  {
    question: "How can I learn more?",
    answer:
      "Potential LPs can complete the investor qualification flow, founders can submit a pitch, and talent, mentors, advisors, and ecosystem partners can register through the community network page. Use the contact page to choose the right path.",
  },
];
